import React from "react";
import { useNavigate } from "react-router-dom";

import { exploreSurveyIntentPath } from "../../../configs/ExploreSurveyConfig";
import { pageConfigBySlug } from "../../../services/nav/NavigationService";
import {
  DashboardTileStatus,
  IDashboardTileResult,
} from "../../../services/zeos-api/contracts/Dashboard";
import {
  dashboardTileHasUsableMetrics,
  formatTileMetric,
  resolveUnlockedBodyState,
} from "../../../services/helpers/DashboardTileHelpers";
import { LockedDashboardTile } from "../tiles/LockedDashboardTile";
import { UnlockedDashboardTile } from "../tiles/UnlockedDashboardTile";
import { useDashboardConnectCta } from "./DashboardConnectProvider";
import {
  DashboardTileKey,
  getDashboardTileStatusCopy,
} from "./dashboardTileStatusCopy";
import { DashboardTileStateView } from "./DashboardTileStateView";
import { MetricGrid } from "./MetricGrid";
import { IStatMetricProps, StatMetricTone } from "./StatMetric";

export interface IDashboardMetricDefinition {
  label: string;
  value: number | null | undefined;
  unit?: string;
  tone?: StatMetricTone;
  abs?: boolean;
  /** Hover explanation shown as an (i) after the label. */
  hint?: string;
  /** Set when this figure uses a default or fallback value: an orange (!) with this tooltip (OI-04). */
  alert?: string;
}

export interface IDashboardMetricTileLockedCopy {
  bodyHeading: string;
  bodyContent: string;
  onExploreClick: () => void;
  buttonTitle?: string;
  secondaryButtonTitle?: string;
  onSecondaryClick?: () => void;
  forceColor?: boolean;
}

interface IProps {
  tileKey: DashboardTileKey;
  title: string;
  image?: string;
  navPath: string;
  unlocked: boolean;
  setUnlocked: (unlocked: boolean) => void;
  lockedCopy: IDashboardMetricTileLockedCopy;
  tileResult?: IDashboardTileResult<unknown>;
  loading?: boolean;
  metrics: IDashboardMetricDefinition[];
  /** Rendered under the metrics, e.g. the coverage disclosure (task-4051). */
  footer?: React.ReactNode;
}

const toStatMetrics = (
  status: DashboardTileStatus | undefined,
  metrics: IDashboardMetricDefinition[]
): IStatMetricProps[] => {
  return metrics.map((metric) => ({
    label: metric.label,
    tone: metric.tone ?? "neutral",
    unit: metric.unit,
    hint: metric.hint,
    alert: metric.alert,
    display: formatTileMetric(status, metric.value, {
      unit: metric.unit,
      abs: metric.abs,
    }),
  }));
};

export const DashboardMetricTile = (props: IProps) => {
  const navigate = useNavigate();
  const connect = useDashboardConnectCta();
  const status = props.tileResult?.status;
  const bodyState = resolveUnlockedBodyState(
    status,
    props.loading,
    dashboardTileHasUsableMetrics(props.metrics)
  );
  const statusCopy = getDashboardTileStatusCopy(
    props.tileKey,
    status,
    props.tileResult?.message,
    props.tileResult?.reason
  );

  const handleStateCta = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (connect) {
      connect.handleCta(statusCopy);
      return;
    }
    const exploreSlug = pageConfigBySlug("explore")?.slug || "explore";
    navigate(exploreSurveyIntentPath(exploreSlug, statusCopy.exploreDomain));
  };

  if (!props.unlocked) {
    return (
      <LockedDashboardTile
        image={props.image}
        title={props.title}
        bodyHeading={props.lockedCopy.bodyHeading}
        bodyContent={props.lockedCopy.bodyContent}
        unlocked={props.unlocked}
        setUnlocked={props.setUnlocked}
        onExploreClick={props.lockedCopy.onExploreClick}
        buttonTitle={props.lockedCopy.buttonTitle}
        secondaryButtonTitle={props.lockedCopy.secondaryButtonTitle}
        onSecondaryClick={props.lockedCopy.onSecondaryClick}
        forceColor={props.lockedCopy.forceColor}
      />
    );
  }

  const overviewMetrics = (
    <div className="flex flex-col items-center justify-center w-full h-full">
      <MetricGrid metrics={toStatMetrics(status, props.metrics)} />
      {props.footer ?? null}
    </div>
  );

  const stateView = (
    <DashboardTileStateView
      heading={statusCopy.heading}
      body={statusCopy.body}
      ctaLabel={statusCopy.ctaLabel}
      onCtaClick={handleStateCta}
    />
  );

  const bodyForState = () => {
    switch (bodyState) {
      case "not_connected":
      case "no_asset":
      case "error":
      case "partial":
        return stateView;
      case "loading":
        return null; // UnlockedDashboardTile shows spinner
      case "metrics":
      default:
        return overviewMetrics;
    }
  };

  return (
    <UnlockedDashboardTile
      image={props.image}
      navPath={props.navPath}
      title={props.title}
      loading={bodyState === "loading"}
      unlocked={props.unlocked}
      setUnlocked={props.setUnlocked}
    >
      {bodyForState()}
    </UnlockedDashboardTile>
  );
};
